import { AnyAction, Store } from '@reduxjs/toolkit';

import { CFD_EVENTS, PRINTER_EVENTS } from '~/main/namespace';
import {
  PRINTER_FORMATS,
  PRINTER_KINDS,
  PrintMessage,
  RECEIPT_PRINTER_BRANDS,
} from '~/main/printer/types';
import { LoggerScope } from '~/main/utils/logger';
import { cfdActions } from '~/renderer/store/cfd';
import { connectionActions } from '~/renderer/store/connection';
import { CONNECTION_STATUS, ConnectionError } from '~/renderer/store/connection/types';
import { printersActions } from '~/renderer/store/printers';
import { receiptActions } from '~/renderer/store/receipt';
import { AppState } from '~/renderer/store/types';
import { isBrowserCFD, isRenderProcess } from '~/renderer/utils';
import Logger from '~/renderer/utils/logger';
import { transformHtmlImages } from '~/renderer/utils/transform-html';
import { HUB_MESSAGES } from '~/types/socket';

import { CFD_MESSAGES, PRINTER_MESSAGES } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MessageData<Data = any> = { data?: Data };

export const socketActionCreator = (
  publish: (message: string, messageData?: string | Record<string, unknown> | undefined) => void,
  store: Store<AppState, AnyAction>,
  transport: LoggerScope.ABLY | LoggerScope.LOCAL_SERVER,
) => {
  return {
    /** Handles the case where we have a browser CFD connected and we'd like
     * to replace it with a desktop CFD.
     */
    onNewCFD: () => {
      if (!isRenderProcess) window.close();
    },

    onOpenCashDrawer: () => {
      window.ipcRenderer?.send(PRINTER_EVENTS.OPEN_CASH_DRAWER);
    },
    updateChannelName: (data: { channelName: string }) => {
      if (data.channelName && store.getState().connection.channelName !== data.channelName) {
        store.dispatch(connectionActions.updateConnectionChannelName(data.channelName));
      }
    },
    onCFDClose: () => {
      window.ipcRenderer?.send(CFD_EVENTS.CLOSE_CFD);
    },
    onSaleUpdated: (message: MessageData) => {
      const { sale, tip = null, paymentState = null } = message.data;
      store.dispatch(receiptActions.updateSale({ sale, saleId: sale.saleID, tip, paymentState }));
      Logger({ scope: LoggerScope.CFD, transport }).debug({
        msg: CFD_MESSAGES.SALE_UPDATED,
        saleId: sale?.saleID,
      });
    },

    onStartTipFlow: (message: MessageData) => {
      const { sale, tip = null } = message.data;
      store.dispatch(receiptActions.updateIsSaleVisible(true));
      store.dispatch(receiptActions.updateSale({ sale, saleId: sale?.saleID, tip }));
      Logger({ scope: LoggerScope.CFD, transport }).debug({
        msg: CFD_MESSAGES.TIP_FLOW_START,
        saleId: sale?.saleID,
      });
    },

    onSaleCompleted: (message: MessageData) => {
      const { sale, tip = null } = message.data;
      store.dispatch(receiptActions.updateSaleCompleted({ sale, saleId: sale.saleID, tip }));
      Logger({ scope: LoggerScope.CFD, transport }).debug({ msg: CFD_MESSAGES.SALE_COMPLETED });
    },
    onTerminalPaymentApproved: () => {
      store.dispatch(receiptActions.updatePaymentStatusComplete());
      Logger({ scope: LoggerScope.CFD, transport }).debug({ msg: CFD_MESSAGES.COMPLETE_PAYMENT });
    },
    onHideSale: () => {
      store.dispatch(receiptActions.updateIsSaleVisible(false));
      Logger({ scope: LoggerScope.CFD, transport }).debug({ msg: CFD_MESSAGES.HIDE_SALE });
    },

    onShowSale: () => {
      store.dispatch(receiptActions.updateIsSaleVisible(true));
      Logger({ scope: LoggerScope.CFD, transport }).debug({ msg: CFD_MESSAGES.SHOW_SALE });
    },

    onConfigUpdated: (message: MessageData) => {
      const { config } = message.data;

      store.dispatch(cfdActions.updateOptions(config));
      Logger({ scope: LoggerScope.HUB, transport }).debug({
        msg: 'Received configuration from retail',
        ...config,
      });
    },
    setConnected: () => {
      store.dispatch(
        connectionActions.updateAblyConnectionStatus({ status: CONNECTION_STATUS.CONNECTED }),
      );
    },

    setDisconnected: (reason?: ConnectionError) => {
      store.dispatch(
        connectionActions.updateAblyConnectionStatus({
          status: CONNECTION_STATUS.DISCONNECTED,
          error: reason as ConnectionError,
        }),
      );
    },
    setLocalServerConnected: () => {
      store.dispatch(connectionActions.updateLocalServerConnectionStatus('connected'));
    },

    setLocalServerDisconnected: () => {
      store.dispatch(connectionActions.updateLocalServerConnectionStatus('disconnected'));
    },

    setReady: () => {
      store.dispatch(
        connectionActions.updateAblyConnectionStatus({ status: CONNECTION_STATUS.READY }),
      );
    },

    onConnectedToRetail: (shouldGetConfig: boolean) => {
      const { default: defaultPrinters } = store.getState().printers;
      const { selectedDisplay, isFullscreen } = store.getState().displays;
      const { localServerStatus } = store.getState().connection;

      if (localServerStatus === 'connected' && transport === LoggerScope.ABLY) {
        return;
      }

      if (shouldGetConfig) {
        publish(HUB_MESSAGES.GET_CONFIG, {});
      }

      if (isBrowserCFD && !selectedDisplay) {
        return;
      }

      const devices = {
        ...(isRenderProcess ? { defaultPrinters } : null),
        selectedDisplay: isFullscreen || isBrowserCFD ? selectedDisplay : {},
        ...(isRenderProcess ? { version: process.env.HUB_VERSION } : null),
      };

      Logger({ scope: LoggerScope.HUB, transport }).debug({
        msg: `Connected - Sending updated devices list to retail`,
        ...devices,
      });

      publish(HUB_MESSAGES.DEVICES, devices);
    },
    onPrint: (message: MessageData) => {
      const {
        kind,
        data,
        format,
        size,
        openCashDrawer,
        copies,
        executionTimes = {},
        analyticsData = {},
      }: PrintMessage = message.data;

      Logger({ scope: LoggerScope.PRINTER, transport }).debug({
        msg: PRINTER_MESSAGES.PRINT,
        data: message.data,
      });

      executionTimes.HUB_RECEIVED_DATA = Date.now();

      const defaultPrinter = store.getState().printers.default?.[kind];

      if (!defaultPrinter) {
        const noPrinter = `No ${kind.toLowerCase()} printer connected`;
        Logger({ scope: LoggerScope.PRINTER }).error({ err: new Error(noPrinter), message });
        publish(PRINTER_MESSAGES.PRINT_ERROR, noPrinter);
        return;
      }

      if (
        kind === PRINTER_KINDS.RECEIPT &&
        defaultPrinter.kind === PRINTER_KINDS.RECEIPT &&
        format === PRINTER_FORMATS.HTML &&
        typeof data === 'string'
      ) {
        transformHtmlImages(data, defaultPrinter.brand as RECEIPT_PRINTER_BRANDS)
          .then((html: string) => {
            executionTimes.HUB_TRANSFORM_HTML_IMAGES = Date.now();
            store.dispatch(
              printersActions.updatePrintersPreview({
                html,
                kind,
                executionTimes,
                openCashDrawer,
                copies,
                analyticsData,
              }),
            );
          })
          .catch(err => {
            Logger({ scope: LoggerScope.PRINTER }).error({
              msg: 'An error occurred while transforming HTML image',
              err,
            });
          });
      } else if (kind === PRINTER_KINDS.LABEL && format === PRINTER_FORMATS.ZPL) {
        window.ipcRenderer.send(PRINTER_EVENTS.PRINT, {
          data,
          partialCuts: [],
          kind,
          size: size?.toUpperCase(),
          executionTimes,
          openCashDrawer: false,
          format,
          analyticsData,
        });
      } else {
        const unsupportedFormat = `${format} not supported`;
        Logger({ scope: LoggerScope.PRINTER }).error({
          err: new Error(unsupportedFormat),
          message,
        });
        publish(PRINTER_MESSAGES.PRINT_ERROR, unsupportedFormat);
      }
    },
    onCustomerStart: () => {
      store.dispatch(
        receiptActions.setCustomerInfoVisibility({
          isCustomerInfoViewVisible: true,
          isSaleVisible: false,
        }),
      );
    },
  };
};
